import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton } from '@/components/ui/page-heading';
import { useProgress } from '@/context/progress';
import {
  identityToPersonalInformationWithDefaults,
  type PersonalInformation,
  type PersonalInformationOrDefaults,
  personalInformationAboutYouSchema,
  personalInformationCountryOfResidencySchema,
  personalInformationHomeAddressSchema,
  personalInformationMobilePhoneSchema,
  personalInformationTaxResidencySchema,
  personalInformationToIdentity,
} from '@/features/onboarding/personal-information/schema';
import { mainstreamCountriesQueryOptions, useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { useProfileAgreement } from '@/hooks/queries/use-profile-agreement';
import { FCA_QUESTIONNAIRE_COUNTRY } from '@/hooks/queries/use-profile-fca-questionnaire';
import { useProfileIdentity, useProfileIdentityMutation } from '@/hooks/queries/use-profile-identity';
import { queryClient } from '@/lib/query-client';
import { AgreementKey } from '@/services/api/profile/agreement';
import type { FormStep } from '@/types/components';
import { isJurisdictionPermitted } from '@/utils';
import { AccountType, getAccountType } from '@/utils/account-type';
import { PersonalInformationFormFieldsetAboutYou, PersonalInformationFormFieldsetAboutYouLegend } from './fieldsets/about-you';
import { PersonalInformationFormFieldsetCountryOfResidency, PersonalInformationFormFieldsetCountryOfResidencyLegend } from './fieldsets/country-of-residency';
import { PersonalInformationFormFieldsetHomeAddress, PersonalInformationFormFieldsetHomeAddressLegend } from './fieldsets/home-address';
import { PersonalInformationFormFieldsetMobilePhone, PersonalInformationFormFieldsetMobilePhoneLegend } from './fieldsets/mobile-phone';
import { PersonalInformationFormFieldsetTaxResidency, PersonalInformationFormFieldsetTaxResidencyLegend } from './fieldsets/tax-residency';

const FORM_STEPS = {
  COUNTRY_OF_RESIDENCY: {
    Component: PersonalInformationFormFieldsetCountryOfResidency,
    Legend: PersonalInformationFormFieldsetCountryOfResidencyLegend,
    schema: personalInformationCountryOfResidencySchema,
  },
  ABOUT_YOU: {
    Component: PersonalInformationFormFieldsetAboutYou,
    Legend: PersonalInformationFormFieldsetAboutYouLegend,
    schema: personalInformationAboutYouSchema,
  },
  HOME_ADDRESS: {
    Component: PersonalInformationFormFieldsetHomeAddress,
    Legend: PersonalInformationFormFieldsetHomeAddressLegend,
    schema: personalInformationHomeAddressSchema,
  },
  MOBILE_PHONE: {
    Component: PersonalInformationFormFieldsetMobilePhone,
    Legend: PersonalInformationFormFieldsetMobilePhoneLegend,
    schema: personalInformationMobilePhoneSchema,
  },
  TAX_RESIDENCY: {
    Component: PersonalInformationFormFieldsetTaxResidency,
    Legend: PersonalInformationFormFieldsetTaxResidencyLegend,
    schema: personalInformationTaxResidencySchema,
  },
} satisfies Record<string, FormStep<PersonalInformation>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const findInitialStep = async (personalInformation: PersonalInformationOrDefaults) => {
  const countries = await queryClient.ensureQueryData(mainstreamCountriesQueryOptions);
  const results = await Promise.all(STEP_SCHEMAS.map((schema) => schema.safeParseAsync(personalInformation)));
  const firstIncompleteStep = results.findIndex(({ success }) => !success);

  // Starting past the country of residency step would skip its forbidden jurisdiction redirect
  if (firstIncompleteStep <= 0 || !isJurisdictionPermitted(personalInformation.countryOfResidency, countries ?? {})) return 0;

  return firstIncompleteStep;
};

const PersonalInformationFormCurrentStep = () => {
  const { setProgress } = useProgress();
  const { currentStep, totalSteps } = useGuardedFormStepper<PersonalInformation>();
  const Step = STEPS[currentStep];

  useEffect(() => {
    return setProgress(currentStep / totalSteps);
  }, [currentStep, setProgress, totalSteps]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
PersonalInformationFormCurrentStep.displayName = 'PersonalInformationFormCurrentStep';

const PersonalInformationFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<PersonalInformation>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
PersonalInformationFormCurrentStepLegend.displayName = 'PersonalInformationFormCurrentStepLegend';

const PersonalInformationFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<PersonalInformation>();

  const handlePrevStep = useCallback(() => prevStep(), [prevStep]);

  if (currentStep === 0) return null;

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
PersonalInformationFormBackButton.displayName = 'PersonalInformationFormBackButton';

const PersonalInformationFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { isLastStep, nextStep } = useGuardedFormStepper<PersonalInformation>();
  const { t } = useTranslation();

  const handleNextStep = useCallback(() => nextStep(), [nextStep]);

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={handleNextStep}>
        <span className={isPending ? 'invisible' : ''}>{t(isLastStep ? 'buttons.confirmAndContinue' : 'buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
PersonalInformationFormFooter.displayName = 'PersonalInformationFormFooter';

const PersonalInformationFormForm = ({ defaultValues, initialStep }: { defaultValues: PersonalInformationOrDefaults; initialStep: number }) => {
  const { data: countries } = useMainstreamCountries();
  const { isPending, mutate } = useProfileIdentityMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onError = () => {
    toast({
      description: t('fundamentals.defaultError.message'),
      variant: 'destructive',
    });
  };

  const onSubmit = async (data: PersonalInformation) => {
    try {
      const identity = await personalInformationToIdentity.parseAsync(data);

      mutate(identity, {
        onError,
        onSuccess: () => {
          const countryOfResidency = identity.addressOfResidency.country;

          // This is somewhat redundant since we redirect from the fieldset, but we'll leave it here for completeness
          if (!isJurisdictionPermitted(countryOfResidency, countries ?? {})) {
            navigate(`/onboarding/personal-information/forbidden-jurisdiction/${countryOfResidency}`, { replace: true });
          } else {
            navigate(countryOfResidency === FCA_QUESTIONNAIRE_COUNTRY && getAccountType() !== AccountType.FUTURES ? '/onboarding/fca-questionnaire' : '/onboarding/verification', {
              replace: true,
            });
          }
        },
      });
    } catch {
      onError();
    }
  };

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} initialStep={initialStep} mode="onTouched" steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <PersonalInformationFormCurrentStepLegend />
        <PersonalInformationFormBackButton />
        <PageCard size="lg" bordered fixedFooter>
          <PersonalInformationFormCurrentStep />
          <PersonalInformationFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
PersonalInformationFormForm.displayName = 'PersonalInformationFormForm';

const PersonalInformationForm = () => {
  const { isLoading: isLoadingServicesAgreement, data: servicesAgreement } = useProfileAgreement(AgreementKey.SERVICES_AGREEMENT);
  const { isLoading: isLoadingCountries } = useMainstreamCountries();
  const { isLoading: isLoadingIdentity, data: identity } = useProfileIdentity();
  const [defaultValues, setDefaultValues] = useState<PersonalInformationOrDefaults | undefined>();
  const [initialStep, setInitialStep] = useState<number | undefined>();
  const isLoading = isLoadingServicesAgreement || isLoadingCountries || isLoadingIdentity || !defaultValues || initialStep === undefined;

  useEffect(() => {
    (async () => {
      if (isLoadingIdentity) return;

      const personalInformation = await identityToPersonalInformationWithDefaults.parseAsync(identity);

      setInitialStep(await findInitialStep(personalInformation).catch(() => 0));
      setDefaultValues(personalInformation);
    })();
  }, [identity, isLoadingIdentity]);

  if (isLoading) return <LoadingCard />;

  if (!servicesAgreement?.date) return <Navigate to="/onboarding/services-agreement" replace />;

  return <PersonalInformationFormForm defaultValues={defaultValues} initialStep={initialStep} />;
};
PersonalInformationForm.displayName = 'PersonalInformationForm';

export default PersonalInformationForm;
